import { useMutation, useQuery } from "@tanstack/react-query";
import { TransportPayload } from "../../Constants/Interfaces/payloadInterface/TransportPayload.interface";
import { deleteApi, get, post, put } from "../../api/client";
import { getValue } from "../../utils/object.utils";
import queryClient from "../../config/queryClient";

const TRANSPORT_KEY = {
  TRANSPORT_LIST: "TRANSPORT_LIST",
  TRANSPORT_LIST_OPTIONS: "TRANSPORT_LIST_OPTIONS",
};

export const useAddTransport = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (err: unknown) => unknown;
}) =>
  useMutation(
    (body: TransportPayload) => {
      return post({
        url: "api/v1/transport/add",
        body: {
          transport_code: getValue(body, "transport_code"),
          route_name: getValue(body, "route_name"),
          vehicle_no: getValue(body, "vehicle_no"),
          driver_name: getValue(body, "driver_name"),
          driver_contact: getValue(body, "driver_contact"),
          driver_license: getValue(body, "driver_license"),
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [TRANSPORT_KEY.TRANSPORT_LIST],
        });

        onSuccess();
      },
      onError,
    }
  );

export const useGetTransportOptions = () =>
  useQuery([TRANSPORT_KEY.TRANSPORT_LIST_OPTIONS], () => {
    return get({ url: "/api/v1/transport/options" });
  });

export const useGetTransport = () =>
  useQuery([TRANSPORT_KEY.TRANSPORT_LIST], () => {
    return get({ url: "/api/v1/transport" });
  });

export const useUpdateTransport = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (err: Error) => unknown;
}) => {
  return useMutation(
    ({
      transportId,
      body,
    }: {
      transportId: number;
      body: TransportPayload;
    }) => {
      return put({
        url: "api/v1/transport",
        body: {
          transport_code: getValue(body, "transport_code"),
          route_name: getValue(body, "route_name"),
          vehicle_no: getValue(body, "vehicle_no"),
          driver_name: getValue(body, "driver_name"),
          driver_contact: getValue(body, "driver_contact"),
          driver_license: getValue(body, "driver_license"),
        },
        params: {
          transportId,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [TRANSPORT_KEY.TRANSPORT_LIST],
        });

        onSuccess();
      },
      onError,
    }
  );
};

export const useRemoveTransport = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (err: Error) => unknown;
}) => {
  return useMutation(
    (transportId: number) => {
      const includeToken = true;
      return deleteApi(`/api/v1/transport/remove`, includeToken, {
        transportId,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [TRANSPORT_KEY.TRANSPORT_LIST],
        });
        onSuccess();
      },
      onError,
    }
  );
};
